import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from 'types';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { toast } from 'sonner';
import { domainMappings } from '../../config';

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const port = typeof window !== 'undefined' ? window.location.port : '';
const hostnameWithPort = port ? `${hostname}:${port}` : hostname;

const config = domainMappings[hostnameWithPort] || domainMappings[hostname];

const appBaseUrl = config ? config.appUrl : 'http://localhost:3000';
const apiBaseUrl = config ? config.apiUrl : 'http://localhost:3005';

const apiPrefix = '/api/v1/admin';

const baseUrl = apiBaseUrl;

export const imageBaseurl = apiBaseUrl + '/static/media';
export const mediaBaseUrl = apiBaseUrl + '/static/media';

const defaultHeaders = {
  Accept: 'application/json',
};

const prepareHeaders = (headers: any, { getState }) => {
  const token = (getState() as RootState)?.global?.user?.token;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

export const formDataQuery: any = fetchBaseQuery({
  baseUrl,
  // headers: defaultHeaders,
  prepareHeaders,
});

export const formatErrors = (errors: any) => {
  return (
    errors?.response ||
    errors?.messages ||
    errors?.data?.messages ||
    'Something went wrong'
  );
};

let activeRequests = 0;

export const baseQuery = async (args, api, extraOptions) => {
  if (activeRequests === 0) {
    NProgress.start();
  }
  activeRequests++;

  // Create the base query instance using fetchBaseQuery with your configs
  const rawBaseQuery = fetchBaseQuery({
    baseUrl, // Base URL for API requests
    headers: defaultHeaders, // Default headers for all requests
    prepareHeaders, // Optional header preparation function
  });

  try {
    // Perform the actual API call
    const result = await rawBaseQuery(args, api, extraOptions);

    // Global handling of unauthorized (401) responses
    if (result.error?.status === 401) {
      toast.error('Session expired, please login again');

      setTimeout(() => {
        window.localStorage.clear(); // Clear stored tokens or session info
        window.location.href = '/login'; // Redirect user to login page
      }, 1000);

      // Return the result with status for potential use
      return {
        ...result,
        status: 401,
      };
    }

    // If there was an error, attach the HTTP status code to the response
    if (result.error) {
      return {
        ...result,
        status: result.error.status,
      };
    }

    // If the request was successful, attach the HTTP status code
    if (result.data) {
      return {
        ...result,
        status: result.meta?.response?.status, // Extract from meta if available
      };
    }

    // Fallback: return raw result if no data or error
    return result;
  } finally {
    // Decrement the active request count and stop NProgress if no more active requests
    activeRequests--;
    if (activeRequests === 0) {
      NProgress.done();
    }
  }
};

export const HTTP_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE',
};

export const endpoints = {
  baseUrl,
  appBaseUrl,
  apiBaseUrl,
  mediaBaseUrl,
  DELETE_FOLDER: `${apiPrefix}/folder/delete`,
  DELETE_FILE: `${apiPrefix}/folder/file/delete`,
  GET_FILE: `${apiPrefix}/folder/file`,
  FOLDER_PATH: `${apiPrefix}/folders/path`,
  UPDATE_FILE: `${apiPrefix}/file/update`,
  SEARCH_FILES: `${apiPrefix}/files/search`,
  BULK_CREATE_FOLDERS: `${apiPrefix}/folder/bulk/create`,
  FOLDER_FILES_BULK_DELETE: `${apiPrefix}/folder-files/delete-many`,
  BULK_CUT_COPY_PASTE: `${apiPrefix}/files/bulk-cut-copy-paste`,
  SEARCH_ALL_FILES: `${apiPrefix}/files/search/all`,
  GENERATE_SEO_TAGS_WITH_AI: `${apiPrefix}/files/seo`,
  UPDATE_FOLDER: `${apiPrefix}/folder/update`,
  DOWNLOAD_PACKAGES: `${apiPrefix}/download-packages`,
  CUT_COPY_FILE: `${apiPrefix}/file/move`,
  CUT_COPY_FOLDER: `${apiPrefix}/folder/move`,
  USERS: `${apiPrefix}/users`,
  USER_PACKAGES: `${apiPrefix}/user-packages`,
  USERS_ACCOUNT: `${apiPrefix}/users/account`,
  ADMINS: `${apiPrefix}/admins`,

  ORDERS: `${apiPrefix}/orders`,
  CONFIRM_ORDER: `${apiPrefix}/orders/confirm-order`,
  CANCEL_ORDER: `${apiPrefix}/orders/cancel-order`,
  TRANSACTIONS: `${apiPrefix}/transactions`,
  FILES_UPLOAD: `${apiPrefix}/files/upload`,
  PAGES: `${apiPrefix}/pages`,
  TEAMS: `${apiPrefix}/teams`,
  TEAMS_POSITIONS: `${apiPrefix}/teams/positions/update`,
  TEAMS_UPLOAD_PHOTO: `${apiPrefix}/teams/upload-photo`,
  BLOGS: `${apiPrefix}/blogs`,
  BLOGS_CATEGORIES: `${apiPrefix}/blogs/categories`,
  BLOGS_TAGS: `${apiPrefix}/blogs/tags`,
  REQUEST_FILE: `${apiPrefix}/request-file/list`,
  LEADS: `${apiPrefix}/leads`,
  AGENTS: `${apiPrefix}/agents`,
  REVIEWS: `${apiPrefix}/reviews`,
  SOCIAL_LINKS: `${apiPrefix}/social-links`,
  SITEMAP: `${apiPrefix}/sitemap`,
  DISCOUNTS: `${apiPrefix}/discounts`,
  COUPONS: `${apiPrefix}/coupons`,
  COUPONS_CREATE: `${apiPrefix}/coupons/create`,
  COUPONS_UPDATE: `${apiPrefix}/coupons/update`,
  COUPONS_DELETE: `${apiPrefix}/coupons/delete`,
  COUPONS_USAGE: `${apiPrefix}/coupons/usage`,
  COUPONS_DOWNLOAD_EXCEL: `${apiPrefix}/reports/coupons/all/download`,
  COUPONS_USAGE_REPORT: `${apiPrefix}/reports/coupons/usage`,
  DOWNLOAD_PACKAGES_PURCHASES_REPORT: `${apiPrefix}/download-packages/purchases`,
  DOWNLOAD_PACKAGE_ORDERS: `${apiPrefix}/orders/download-packages`,
  MEDIA: `${apiPrefix}/media`,
  CURRENCIES: `${apiPrefix}/currencies`,
  COUNTRIES: `/api/countries`,
  DOWNLOADS_HISTORY: `${apiPrefix}/downloads-history`,
  MENUS: `${apiPrefix}/menu`,
  SEO: `${apiPrefix}/seo`,
  BANNERS: `${apiPrefix}/banner`,
  PRODUCTS_ATTRIBUTE: `${apiPrefix}/products/attributes`,
  PRODUCTS: `${apiPrefix}/products`,
  PRODUCTS_CATEGORIES: `${apiPrefix}/products/categories`,
  PRODUCTS_CATEGORIES_LIST: `${apiPrefix}/products/categories/list`,
  PRODUCTS_CATEGORIES_TREE: `${apiPrefix}/products/categories/tree`,
  PRODUCTS_CATEGORIES_PATH: `${apiPrefix}/products/categories/path`,
  PRODUCTS_CATEGORIES_CREATE: `${apiPrefix}/products/categories/create`,
  PRODUCTS_CATEGORIES_UPDATE: `${apiPrefix}/products/categories/update`,
  PRODUCTS_CATEGORIES_MOVE: `${apiPrefix}/products/categories/move`,
  PRODUCTS_CATEGORIES_DELETE: `${apiPrefix}/products/categories/delete`,
  PRODUCTS_CATEGORIES_REBUILD: `${apiPrefix}/products/categories/rebuild-nested-set`,
  PRODUCTS_TAGS: `${apiPrefix}/products/tags`,
  PRODUCTS_VARIANTS: `${apiPrefix}/products/variants`,
  VIDEOS: `${apiPrefix}/videos`,
  SETTINGS: `${apiPrefix}/settings`,
  OPTIONS: `/api/settings`,
  PROFILE: `${apiPrefix}/auth`,
  REPORTS: `${apiPrefix}/reports`,
  BACKUP_CODES: `${apiPrefix}/auth/verify-backup-code`,

  COURSE: `${apiPrefix}/course`,
  COURSES: `${apiPrefix}/courses`,
  COURSE_CATEGORIES: `${apiPrefix}/courses/categories`,
  COURSE_TAGS: `${apiPrefix}/courses/tags`,

  // dashboard charts

  CHART_USERS: `${apiPrefix}/dashboard/charts/users`,
  CHART_ORDERS: `${apiPrefix}/dashboard/charts/orders`,
  CHART_DOWNLOADS: `${apiPrefix}/dashboard/charts/downloads`,
  CHART_TRANSACTIONS: `${apiPrefix}/dashboard/charts/transactions`,
  GA_OVERVIEW: `${apiPrefix}/dashboard/google-analytics`,
  GA_ACQUISITION: `${apiPrefix}/dashboard/google-analytics/user-acquisition`,
  GA_PAGE_VIEWS: `${apiPrefix}/dashboard/google-analytics/page-views-over-time`,
  GA_DEMOGRAPHICS: `${apiPrefix}/dashboard/google-analytics/user-demographics`,
  GA_TOP_PAGES: `${apiPrefix}/dashboard/google-analytics/top-pages`,
  GA_ACTIVE_USERS: `${apiPrefix}/dashboard/google-analytics/active-users`,
  GA_ACTIVE_USERS_HISTORY: `${apiPrefix}/dashboard/google-analytics/active-users/history`,
  GA_CONFIG: `${apiPrefix}/dashboard/google-analytics/config`,
  GA_CONFIG_TEST: `${apiPrefix}/dashboard/google-analytics/config/test`,

  // dashboard
  DASHBOARD_STATS: `${apiPrefix}/dashboard/stats`,
  RECENT_USERS: `${apiPrefix}/dashboard/recent-users`,
  RECENT_ORDERS: `${apiPrefix}/dashboard/recent-orders`,
  RECENT_REQUEST_FILES: `${apiPrefix}/dashboard/recent-request-files`,
  TOP_FILE_DOWNLOADS: `${apiPrefix}/dashboard/top-downloads`,
  RECENT_DOWNLOADS: `${apiPrefix}/dashboard/recent-downloads`,
  RECENT_WALLET_TRANSACTIONS: `${apiPrefix}/dashboard/recent-wallet-transactions`,
  RECENT_ENQUIRY: `${apiPrefix}/dashboard/recent-enquiry`,
  RECENT_TRANSACTIONS: `${apiPrefix}/dashboard/recent-transactions`,
  PAYMENT_GATEWAYS: `${apiPrefix}/payment-gateways`,

  // api manager
  API_KEYS: `${apiPrefix}/api-manager`,

  // Email Service
  EMAIL_SERVICE: `${apiPrefix}/email-service`,
  EMAIL_PROVIDERS: `${apiPrefix}/email-service/unified/configs`,
  EMAIL_CONFIG: `${apiPrefix}/email-service/config`,
  EMAIL_LOGS: `${apiPrefix}/email-service/logs`,
  EMAIL_STATS: `${apiPrefix}/email-service/stats`,

  ACM_ROLES: `${apiPrefix}/roles`,
  ACM: `${apiPrefix}/acl`,

  // Error Logs
  ERROR_LOGS: `${apiPrefix}/error-logs`,
  ERROR_LOGS_STATS: `${apiPrefix}/error-logs/stats`,
  ERROR_LOGS_RESOLVE: `${apiPrefix}/error-logs/resolve`,
  ERROR_LOGS_BULK_DELETE: `${apiPrefix}/error-logs/bulk/old`,

  // Admin Notifications
  NOTIFICATIONS: `${apiPrefix}/notifications`,
  NOTIFICATIONS_RECENT: `${apiPrefix}/notifications/recent`,
  NOTIFICATIONS_UNREAD_COUNT: `${apiPrefix}/notifications/unread-count`,
  NOTIFICATIONS_STATS: `${apiPrefix}/notifications/stats`,
  NOTIFICATIONS_MARK_ALL_READ: `${apiPrefix}/notifications/mark-all-read`,

  // Tax Management
  TAXES: `${apiPrefix}/taxes`,
  ADD_TAX: `${apiPrefix}/taxes/create`,
  DELETE_TAX: `${apiPrefix}/taxes/delete`,

  // Supplier Management
  SUPPLIERS: `${apiPrefix}/suppliers`,
  SUPPLIERS_SEARCH: `${apiPrefix}/suppliers/search`,
  SUPPLIERS_STATS: `${apiPrefix}/suppliers/stats`,
  SUPPLIERS_REPORTS: `${apiPrefix}/suppliers/reports`,

  // Coupon Discounts
  COUPON_DISCOUNTS: `${apiPrefix}/coupon-discounts`,

  // Contact Us Enquiry
  CONTACT_US_ENQUIRY: `${apiPrefix}/leads`,

  // Invoices
  INVOICES: `${apiPrefix}/invoices`,
  INVOICE_PDF: `${apiPrefix}/invoices`,

  // Offline Payment Methods
  OFFLINE_PAYMENT_METHODS: `${apiPrefix}/offline-payment-methods`,
  OFFLINE_PAYMENT_METHODS_BY_CATEGORY: `${apiPrefix}/offline-payment-methods`,
  OFFLINE_PAYMENT_METHODS_SPECIFIC: `${apiPrefix}/offline-payment-methods`,
  OFFLINE_PAYMENT_METHODS_CREATE: `${apiPrefix}/offline-payment-methods`,
  OFFLINE_PAYMENT_METHODS_UPDATE: `${apiPrefix}/offline-payment-methods`,
  OFFLINE_PAYMENT_METHODS_DELETE: `${apiPrefix}/offline-payment-methods`,
  OFFLINE_PAYMENT_METHODS_TOGGLE_STATUS: `${apiPrefix}/offline-payment-methods`,

  login: {
    url: `${apiPrefix}/auth/login`,
    method: HTTP_METHODS.POST,
  },
  addFile: {
    url: `${apiPrefix}/file/add`,
    method: HTTP_METHODS.POST,
  },
  generateSeoTagsWithAi: {
    url: `${apiPrefix}/file/seo`,
    method: HTTP_METHODS.POST,
  },
  folders: {
    url: `${apiPrefix}/folders`,
    method: HTTP_METHODS.GET,
  },
  addFolder: {
    url: `${apiPrefix}/folder/add`,
    method: HTTP_METHODS.POST,
  },
  files: {
    url: `${apiPrefix}/folder/files`,
    method: HTTP_METHODS.GET,
  },
  register: {
    url: `${apiPrefix}/auth/user/signup`,
    method: HTTP_METHODS.POST,
  },
  sendOtp: {
    url: `${apiPrefix}/send-otp`,
    method: HTTP_METHODS.POST,
  },
  verifyOtp: {
    url: `${apiPrefix}/auth/user/otp/verify`,
    method: HTTP_METHODS.POST,
  },
  resetPassword: {
    url: `${apiPrefix}/auth/user/reset-password`,
    method: HTTP_METHODS.POST,
  },
  forgotPasword: {
    url: `${apiPrefix}/auth/user/forgot-password`,
    method: HTTP_METHODS.POST,
  },
  validateUsername: {
    url: `${apiPrefix}/auth/user/signup/validate`,
    method: HTTP_METHODS.POST,
  },
  uploadMedia: {
    url: `${apiPrefix}/files/upload`,
    method: HTTP_METHODS.POST,
  },
  createJob: {
    url: `${apiPrefix}/job/create`,
    method: HTTP_METHODS.POST,
  },
  getJobs: {
    url: `${apiPrefix}/jobs`,
    method: HTTP_METHODS.GET,
  },
  customer: {
    url: `${apiPrefix}/customer/create`,
    method: HTTP_METHODS.POST,
  },
  customers: {
    url: `${apiPrefix}/customers`,
    method: HTTP_METHODS.GET,
  },
  users: {
    url: `${apiPrefix}/users`,
    method: HTTP_METHODS.GET,
  },
  orders: {
    url: `${apiPrefix}/users`,
    method: HTTP_METHODS.GET,
  },
  jobReceipt: `${baseUrl}${apiPrefix}/job/receipt/`,

  // Inventory Management Endpoints
  INVENTORY_OVERVIEW: `${apiPrefix}/inventory/overview`,
  INVENTORY_PRODUCT_DETAILS: `${apiPrefix}/inventory/product`,
  INVENTORY_ADJUST: `${apiPrefix}/inventory/product`,
  INVENTORY_BULK_ADJUST: `${apiPrefix}/inventory/bulk-adjust`,
  INVENTORY_REPORTS: `${apiPrefix}/inventory/reports`,
  INVENTORY_ALERTS: `${apiPrefix}/inventory/alerts`,

  // Telegram Bot Configuration Endpoints
  TELEGRAM_BOT_CONFIG: `${apiPrefix}/telegram-config`,
  TELEGRAM_BOT_CONFIG_MODULES: `${apiPrefix}/telegram-config/modules`,
  TELEGRAM_BOT_CONFIG_BOTS: `${apiPrefix}/telegram-config/bots`,
};
